import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown } from './ui/Dropdown';
import { Chip } from './ui/Chip';
import { PageType, UILocale } from '../types';

interface NavigationProps {
  activePage: PageType;
  onNavigate: (page: PageType) => void;
  currentLanguage: UILocale;
  onLanguageChange: (language: UILocale) => void;
}

const Navigation = ({ activePage, onNavigate, currentLanguage, onLanguageChange }: NavigationProps) => {
  const { t } = useTranslation();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const isServicePage = activePage === 'maintenance' || activePage === 'ansul-commissioning';

  const handleDrawerNavigate = (page: PageType) => {
    setIsDrawerOpen(false);
    onNavigate(page);
  };

  const handleDrawerLanguageChange = (language: UILocale) => {
    onLanguageChange(language);
  };

  // Close drawer on Escape key
  const handleEscapeKey = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && isDrawerOpen) {
      setIsDrawerOpen(false);
    }
  }, [isDrawerOpen]);

  useEffect(() => {
    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [handleEscapeKey]);

  return (
    <header className="app-header">
      <div className="header-content">
        <div className="header-logo" onClick={() => onNavigate('landing')}>
          <img src="/halton-logo.png" alt="Halton" className="logo-image" />
          <span className="logo-text-divider">|</span>
          <div className="logo-text">
            Service Laama
          </div>
          <Chip label="V2.0" theme="info" size="small" className="logo-chip" />
        </div>

        <nav className="nav-tabs">
          <button
            className={`nav-tab ${activePage === 'landing' ? 'active' : ''}`}
            onClick={() => onNavigate('landing')}
          >
            {t('nav.home')}
          </button>
          <button
            className={`nav-tab ${activePage === 'calculator' ? 'active' : ''}`}
            onClick={() => onNavigate('calculator')}
          >
            {t('nav.calculator')}
          </button>
          <button
            className={`nav-tab ${activePage === 'workorder' ? 'active' : ''}`}
            onClick={() => onNavigate('workorder')}
          >
            {t('nav.workOrder')}
          </button>
          
          {/* Service Reports Dropdown */}
          <Dropdown
            trigger={
              <button className={`nav-tab ${isServicePage ? 'active' : ''}`}>
                {t('nav.serviceReports')}
                <span className="material-symbols-outlined dropdown-arrow" style={{ fontSize: '16px', marginLeft: '4px' }}>
                  expand_more
                </span>
              </button>
            }
            placement="bottom-left"
            className="nav-dropdown"
          >
            <div className="nav-dropdown-menu-content">
              <div className="dropdown-group-label">{t('nav.maintenanceReports')}</div>
              <button
                className={`dropdown-item ${activePage === 'maintenance' ? 'active' : ''}`}
                onClick={() => onNavigate('maintenance')}
              >
                <span className="material-symbols-outlined">description</span>
                {t('nav.maintenance')}
              </button>
              <div className="dropdown-divider" />
              <div className="dropdown-group-label">{t('nav.commissioningReports')}</div>
              <button
                className={`dropdown-item ${activePage === 'ansul-commissioning' ? 'active' : ''}`}
                onClick={() => onNavigate('ansul-commissioning')}
              >
                <span className="material-symbols-outlined">local_fire_department</span>
                {t('ansulCommissioning.nav.title')}
              </button>
            </div>
          </Dropdown>

          <button
            className={`nav-tab ${activePage === 'archive' ? 'active' : ''}`}
            onClick={() => onNavigate('archive')}
          >
            {t('nav.archive')}
          </button>
        </nav>

        <div className="header-actions">
          <div className="language-selector">
            <button
              className={`language-btn ${currentLanguage === 'fi' ? 'active' : ''}`}
              onClick={() => onLanguageChange('fi')}
              title="Suomi"
            >
              FI
            </button>
            <button
              className={`language-btn ${currentLanguage === 'en' ? 'active' : ''}`}
              onClick={() => onLanguageChange('en')}
              title="English"
            >
              EN
            </button>
          </div>
          <button
            className={`btn btn-sm ${activePage === 'settings' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => onNavigate('settings')}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>settings</span>
            {t('nav.settings')}
          </button>
        </div>

        {/* Hamburger Button - visible only on mobile via CSS */}
        <button
          className="hamburger-btn"
          data-testid="hamburger-btn"
          onClick={() => setIsDrawerOpen(!isDrawerOpen)}
          aria-label={isDrawerOpen ? t('nav.closeMenu') : t('nav.openMenu')}
          aria-expanded={isDrawerOpen}
        >
          <span className="material-symbols-outlined">
            {isDrawerOpen ? 'close' : 'menu'}
          </span>
        </button>
      </div>

      {/* Drawer Overlay */}
      <div
        className={`drawer-overlay ${isDrawerOpen ? 'visible' : ''}`}
        data-testid="drawer-overlay"
        onClick={() => setIsDrawerOpen(false)}
      />

      {/* Mobile Drawer */}
      <aside
        className={`mobile-drawer ${isDrawerOpen ? 'open' : ''}`}
        data-testid="mobile-drawer"
        role={isDrawerOpen ? 'dialog' : undefined}
        aria-modal={isDrawerOpen ? true : undefined}
      >
        <div className="drawer-header">
          <div className="drawer-header-logo">
            <img src="/halton-logo.png" alt="Halton" className="logo-image" />
            <span className="logo-text-divider">|</span>
            <div className="logo-text">Service Laama</div>
          </div>
          <button
            className="drawer-close-btn"
            onClick={() => setIsDrawerOpen(false)}
            aria-label={t('nav.closeMenu')}
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div className="drawer-section-label">{t('nav.home')}</div>

        <button
          className={`drawer-nav-item ${activePage === 'landing' ? 'active' : ''}`}
          data-testid="drawer-nav-landing"
          onClick={() => handleDrawerNavigate('landing')}
        >
          <span className="material-symbols-outlined">home</span>
          {t('nav.home')}
        </button>
        <button
          className={`drawer-nav-item ${activePage === 'calculator' ? 'active' : ''}`}
          data-testid="drawer-nav-calculator"
          onClick={() => handleDrawerNavigate('calculator')}
        >
          <span className="material-symbols-outlined">calculate</span>
          {t('nav.calculator')}
        </button>
        <button
          className={`drawer-nav-item ${activePage === 'workorder' ? 'active' : ''}`}
          data-testid="drawer-nav-workorder"
          onClick={() => handleDrawerNavigate('workorder')}
        >
          <span className="material-symbols-outlined">assignment</span>
          {t('nav.workOrder')}
        </button>

        <div className="drawer-divider" />
        <div className="drawer-section-label">{t('nav.serviceReports')}</div>

        <div className="drawer-sub-label">{t('nav.maintenanceReports')}</div>
        <button
          className={`drawer-nav-item ${activePage === 'maintenance' ? 'active' : ''}`}
          data-testid="drawer-nav-maintenance"
          onClick={() => handleDrawerNavigate('maintenance')}
        >
          <span className="material-symbols-outlined">description</span>
          {t('nav.maintenance')}
        </button>

        <div className="drawer-sub-label">{t('nav.commissioningReports')}</div>
        <button
          className={`drawer-nav-item ${activePage === 'ansul-commissioning' ? 'active' : ''}`}
          data-testid="drawer-nav-ansul-commissioning"
          onClick={() => handleDrawerNavigate('ansul-commissioning')}
        >
          <span className="material-symbols-outlined">local_fire_department</span>
          {t('ansulCommissioning.nav.title')}
        </button>

        <div className="drawer-divider" />

        <button
          className={`drawer-nav-item ${activePage === 'archive' ? 'active' : ''}`}
          data-testid="drawer-nav-archive"
          onClick={() => handleDrawerNavigate('archive')}
        >
          <span className="material-symbols-outlined">inventory_2</span>
          {t('nav.archive')}
        </button>

        <div className="drawer-divider" />

        <div className="drawer-section-label">{t('nav.settings')}</div>

        <div className="drawer-language-selector">
          <button
            className={`language-btn ${currentLanguage === 'fi' ? 'active' : ''}`}
            data-testid="drawer-language-fi"
            onClick={() => handleDrawerLanguageChange('fi')}
          >
            FI
          </button>
          <button
            className={`language-btn ${currentLanguage === 'en' ? 'active' : ''}`}
            data-testid="drawer-language-en"
            onClick={() => handleDrawerLanguageChange('en')}
          >
            EN
          </button>
        </div>

        <button
          className={`drawer-nav-item ${activePage === 'settings' ? 'active' : ''}`}
          data-testid="drawer-nav-settings"
          onClick={() => handleDrawerNavigate('settings')}
        >
          <span className="material-symbols-outlined">settings</span>
          {t('nav.settings')}
        </button>
      </aside>
    </header>
  );
};

export default Navigation;
